import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { FilmSlate, UploadSimple, Trash } from "@phosphor-icons/react";
import { Header, AddBtn, Modal, In, SaveBtn, IconBtn } from "./shared";

export default function HighlightsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("upload");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", url: "", thumbnail: "" });

  const load = useCallback(() => api.get("/highlights").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setMode("upload"); setFile(null); setForm({ title: "", description: "", url: "", thumbnail: "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === "upload") {
        if (!file) { toast.error("Choose a file"); setLoading(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("description", form.description);
        await api.post("/highlights/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/highlights/link", { title: form.title, url: form.url, thumbnail: form.thumbnail, description: form.description });
      }
      toast.success("Highlight added"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete highlight?")) return; await api.delete(`/highlights/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="highlights-tab">
      <Header title="Highlights" action={<AddBtn onClick={openNew} label="Add Highlight" testid="add-highlight-btn" />} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((h) => (
          <div key={h.id} className="border border-white/10 bg-[#121212] overflow-hidden" data-testid="highlight-admin-card">
            <div className="aspect-video bg-black flex items-center justify-center">
              {h.media_type === "image" ? <img src={resolveMedia(h.url)} alt="" className="w-full h-full object-cover" /> :
               h.thumbnail ? <img src={h.thumbnail} alt="" className="w-full h-full object-cover" /> :
               <FilmSlate size={40} className="text-white/30" />}
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-display text-lg uppercase truncate">{h.title}</div>
                <div className="text-xs uppercase text-white/40">{h.media_type}</div>
              </div>
              <IconBtn onClick={() => del(h.id)} danger testid="delete-highlight-btn"><Trash size={18} /></IconBtn>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40">No highlights yet.</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Highlight">
        <div className="flex gap-2 mb-5">
          {["upload", "link"].map((m) => (
            <button key={m} data-testid={`hl-mode-${m}`} onClick={() => setMode(m)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all ${mode === m ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/60"}`}>
              {m === "upload" ? "Upload File" : "Video Link"}
            </button>
          ))}
        </div>
        <form onSubmit={save} className="space-y-4">
          <In label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="highlight-title-input" />
          {mode === "upload" ? (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Photo or Video File</label>
              <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-6 cursor-pointer hover:border-[#FF5722] transition-colors justify-center">
                <UploadSimple size={20} className="text-[#FF5722]" />
                <span className="text-sm text-white/60">{file ? file.name : "Click to choose a file"}</span>
                <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="highlight-file-input" />
              </label>
            </div>
          ) : (
            <>
              <In label="Video URL (YouTube etc.)" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="highlight-url-input" />
              <In label="Thumbnail URL (optional)" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} />
            </>
          )}
          <In label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <SaveBtn loading={loading} testid="save-highlight-btn" />
        </form>
      </Modal>
    </div>
  );
}
